import { Component, OnInit } from '@angular/core';
import { BaseComponent, SpinnerComponent } from '@lib/components';
import { AngularMaterialModule } from 'src/app/material.module';
import { AccountType, ApiResponseError, IAccount, ICustomer, ISearchBooleanQuery, ISearchTransactionResponse, ITransaction, ITransactionIndex, IUser, IWallet } from '@lib/interfaces';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TransactionExtendedType } from '@lib/enums';
import { ValueFieldModule } from '@lib/modules';
import { FormBuilder } from '@angular/forms';
import { AccountsService, TransactionService, SidePanelService } from '@lib/services';
import { CustomersService } from '@lib/services/customer/customer.service';
import { finalize } from 'rxjs';
import { TransactionTypePipe } from '@lib/pipes';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { BeneficiarySelectionComponent } from '@pages/send-money/tenant7/components/beneficiary-selection/beneficiary-selection.component';
import { CashInComponent } from '@pages/cash/component/cash-in/cash-in.component';
import { SharedSendMoneyModule } from '../../../send-money/components/send-money/shared-send-money.module';
import { SelectRecipientComponent } from '../super-agent-topup/select-recipient/select-recipient.component';

export enum Status {
    Approved = 'approved',
    Declined = 'declined',
    Settled = 'settled',
}

export interface ITransactionStatus {
    approved: ITransaction;
    declined: ITransaction;
    settled: ITransaction;
    value: string;
}
@Component({
    selector: 'app-neo-home',
    templateUrl: './neo-home.component.html',
    styleUrls: ['./neo-home.component.scss'],
    standalone: true,
    imports: [SharedSendMoneyModule, AngularMaterialModule, SpinnerComponent, ValueFieldModule, TransactionTypePipe],
})
export class NeoHomeComponent extends BaseComponent implements OnInit {
    accounts: IAccount[] = [];
    customer: ICustomer;
    customerId: string;
    agentId: string;
    parentUserId: string;
    agentInfo: IUser;
    agentName: string;
    isSubAgent: string;
    masterAccountId: string;
    masterAccount: IAccount[];
    isLoading = true;
    protected isSuperAgent = false;
    walletForm = this.createWalletForm();
    transactionType = TransactionExtendedType;
    selectedWallet = { currency: this.walletForm.controls.currency.value, balance: 0 };

    protected wallets: any = [];
    protected transactions: ITransactionIndex[] = [];
    protected transactionLoading = true;
    protected transactionGraphLoading = true;
    protected currentDate = new Date();

    constructor(
        private readonly accountsService: AccountsService,
        private readonly snackbar: MatSnackBar,
        protected readonly formBuilder: FormBuilder,
        private readonly dialog: MatDialog,
        private readonly transactionService: TransactionService,
        private readonly router: Router,
        private readonly sidePanel: SidePanelService,
        private readonly customersService: CustomersService,
    ) {
        super();
    }

    ngOnInit() {
        this.isSubAgent = localStorage.getItem('isSubAgent')!;
        this.customerId = localStorage.getItem('userId')!;
        this.parentUserId = localStorage.getItem('parentUserId')!;
        this.agentInfo = JSON.parse(localStorage.getItem('userInfo')!);
        this.agentName = this.agentInfo.first_name + ' ' + this.agentInfo.last_name;
        this.fetchIsSuperAgent();

        this.fetchCustomerAccounts();
        this.addSubscription(
            this.accountsService.reloadWalletList.subscribe({
                next: (value: any) => {
                    if (value) {
                        this.fetchCustomerAccounts();
                    }
                },
            }),
        );
        this.walletForm.controls.currency.valueChanges.subscribe((value) => {
            this.selectedWallet.currency = value;
        });
    }

    // NEOIQ-2427: "Top-up for Agent" is visible only to Super Agents (is_super_agent = true).
    // custom_info isn't part of the cached userInfo in localStorage, so it needs its own fetch —
    // same custom_info bag / string-boolean convention already used for sdk_verification_required
    // in peer-to-peer-confirmation.component.ts.
    protected fetchIsSuperAgent(): void {
        this.addSubscription(
            this.customersService.fetchCustomerDetailsById(this.customerId).subscribe({
                next: (customer) => {
                    this.isSuperAgent = customer.custom_info?.['is_super_agent'] === 'true';
                },
                error: () => {
                    this.isSuperAgent = false;
                },
            }),
        );
    }

    fetchCustomerAccounts() {
        this.accountsService.getAccountsByUserId(this.isSubAgent ? this.parentUserId : this.customerId, this.isSubAgent ? AccountType.MASTER : undefined).subscribe({
            next: ({ data: accounts }) => {
                if (accounts.length === 0) {
                    this.isLoading = false;
                    return;
                }

                const masterAccount = accounts.find((account) => account.type === AccountType.MASTER);

                if (masterAccount) {
                    this.masterAccountId = masterAccount.id;
                    this.masterAccount = [masterAccount];

                    localStorage.setItem('masterAccount', JSON.stringify(this.masterAccount));
                    localStorage.setItem('masterAccountId', masterAccount.id);
                }

                const uniqueBalances = new Map(Object.entries(masterAccount!.balances));

                this.wallets = Array.from(uniqueBalances, ([currency, balance]) => ({
                    currency,
                    balance,
                    selected: currency === this.walletForm.controls.currency.value,
                }));

                this.selectedWallet = this.wallets.find((wallet: any) => wallet.currency === this.walletForm.controls.currency.value) || { currency: this.walletForm.controls.currency.value, balance: 0, selected: true };

                this.wallets = this.wallets.map((wallet: any) => ({
                    ...wallet,
                    selected: wallet.currency === this.selectedWallet.currency,
                }));

                this.accounts = accounts;
                this.searchTransactions();
                this.isLoading = false;
            },
            error: (error: ApiResponseError) => {
                this.isLoading = false;
                this.createApiErrorSnackBar(this.snackbar, error);
            },
        });
    }
    protected get transactionBooleanQuery(): ISearchBooleanQuery {
        return {
            must: [
                {
                    queryObjectType: 'bool',
                    bool: {
                        should: [
                            { queryObjectType: 'term', fieldName: 'account_id1.keyword', fieldValue: this.masterAccountId },
                            { queryObjectType: 'term', fieldName: 'account_id2.keyword', fieldValue: this.masterAccountId },
                        ],
                    },
                },
                {
                    queryObjectType: 'bool',
                    bool: {
                        should: [{ queryObjectType: 'term', fieldName: 'status_code.keyword', fieldValue: '0000' }],
                    },
                },
                {
                    queryObjectType: 'bool',
                    bool: {
                        should: [
                            { queryObjectType: 'term', fieldName: 'transaction_type.keyword', fieldValue: 'WALLET_TRANSFER' },
                            { queryObjectType: 'term', fieldName: 'transaction_type.keyword', fieldValue: 'WALLET_CREDIT' },
                        ],
                    },
                },
            ],
        };
    }

    protected createWalletForm() {
        return this.formBuilder.group({
            currency: this.formBuilder.nonNullable.control('IQD'),
            duration: this.formBuilder.nonNullable.control(this.currentDate),
        });
    }

    protected changeWallet(selectedWalled: string) {
        const wallet = this.wallets.find((wallet: IWallet) => wallet.currency === selectedWalled);

        this.selectedWallet = wallet;
    }

    getInitials(names: string): string {
        const namesArray = names.split(' ');
        const initials = namesArray.map((name) => name.charAt(0).toUpperCase()).join('');
        return initials;
    }

    protected searchTransactions(): void {
        this.addSubscription(
            this.transactionService
                .searchTransactions({ limit: 50, filters: [], boolean_query: this.transactionBooleanQuery })
                .pipe(
                    finalize((): void => {
                        this.transactionLoading = false;
                    }),
                )
                .subscribe({
                    next: ({ data }: ISearchTransactionResponse): void => {
                        this.transactions = data;
                    },
                    error: (error: ApiResponseError): void => {
                        this.transactionLoading = false;
                        this.createApiErrorSnackBar(this.snackbar, error);
                    },
                }),
        );
    }

    formatDate(date: string) {
        if (date.length === 6) {
            return `${new Date(date.substring(0, 2)).toLocaleString('en-US', { month: 'short' })} ${date.substring(2, date.length)}`;
        }
        if (date.length === 8) {
            const currentDate = `${date.substring(2, 4)}/${date.substring(0, 2)}/${date.substring(4, date.length)}`;
            const formattedDate = new Date(currentDate).toLocaleString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
            });
            return formattedDate;
        }
        throw new Error('Invalid date format');
    }

    protected redirectToWalletTransfer() {
        this.router.navigate(['/send-money'], {
            state: { from: 'home' },
        });
    }

    protected openTopUpForAgent(): void {
        this.sidePanel.open(SelectRecipientComponent, {
            data: {
                senderAccounts: this.masterAccount,
                senderInfo: { name: this.agentName, mobile: this.agentInfo.mobile },
            },
        });
    }
}
